/**
 * Smart Tools / Smart Sell marketing + UI copy.
 * Keep language plain — a kid should understand what the bot is doing.
 * Smart Sell is live on production (main): sidebar, footer, header notice,
 * dashboard Create Bot, and `/smart-sell`.
 */

/** Sidebar, footer, header notice, dashboard Create Bot → Smart Sell. */
export const SMART_SELL_NAV_ENABLED = true;

export const SMART_TOOLS_HUB = {
  title: "Smart Tools",
  subtitle:
    "Helpers that watch the chart and act for you — starting with Smart Sell.",
  eyebrow: "Live on the dashboard",
  note: "Create from Smart Sell, then manage bots on your dashboard — Start, History, and Refund.",
};

/** Short labels reused on form, card, and landing. */
export const SMART_SELL_COPY = {
  title: "Smart Sell",
  tagline: "Sell when someone else buys — not all at once.",
  subtitle:
    "When a real buyer spends SOL on your chart and market cap is at your target, Smart Sell sells a slice of that buy from your bag — through fresh wallets — and sends the SOL back to your deposit.",
  feeHint:
    "Send the tokens you want to sell to this deposit address. Swaps are gasless-first — no SOL deposit required. Use Refund later to take sold SOL home, or stop and fully refund.",
  fundTitle: "Add funds to your deposit wallet",
  fundIntro:
    "Copy this address and send the tokens you want Smart Sell to sell from. Private keys stay on the server — withdraw with Refund on the bot card.",
  fundAck: "I copied the deposit address and will send tokens",
  continueCta: "Continue to dashboard",
  minBuyHint: "Tiny buys under 0.1 SOL are ignored.",
  oneLiner:
    "Someone buys → we sell a slice → SOL comes back to your deposit wallet.",
  createCta: "Create Smart Sell bot",
  openCta: "Open Smart Sell",
  guideCta: "Read the Smart Sell guide",
};

/** Kid-friendly “how it works” for the landing page. */
export const SMART_SELL_HOW_IT_WORKS = [
  {
    step: "1",
    title: "Pick your token and chart",
    body: "Paste your mint, then choose the pool (the chart) Smart Sell should watch.",
  },
  {
    step: "2",
    title: "Set your “start selling” number",
    body: "Enter a target market cap in USD. Below that number, the bot waits. At or above it, qualifying buys can trigger a sell.",
  },
  {
    step: "3",
    title: "Choose wallets to skip (optional)",
    body: "Add team or bot wallets to the whitelist. Buys from those addresses never trigger a sell.",
  },
  {
    step: "4",
    title: "Fund on the bot card, then Start",
    body: "After create you land on the dashboard. Copy the deposit address (or tap Add funds) on the bot card, send tokens, then Start. Refund sold SOL later, or stop and fully refund.",
  },
];

/** Simple “what you will see” cards for landing + guide teasers. */
export const SMART_SELL_OUTPUT_CARDS = [
  {
    id: "sold",
    title: "How much it sold",
    body: "Total tokens sold, how many sells happened, and how much SOL came back.",
  },
  {
    id: "balance",
    title: "How much wallets have",
    body: "Tokens still in the deposit bag, plus any sold SOL that came back.",
  },
  {
    id: "wallets",
    title: "How many wallets we chose",
    body: "Whitelist wallets you chose to skip, plus unique sell wallets used for each reaction.",
  },
];

export const SMART_SELL_SIMPLE_RULES = [
  {
    title: "Only real buys",
    body: "A buy must be at least 0.1 SOL and not from a whitelisted wallet.",
  },
  {
    title: "Only above your target",
    body: "If live market cap is under your USD target, Smart Sell does nothing.",
  },
  {
    title: "About 25% of the buy",
    body: "For each qualifying buy, it aims to sell roughly a quarter of that buy’s SOL size in tokens — capped so one sell cannot empty the bag.",
  },
  {
    title: "Fresh sell wallets",
    body: "Each sell can use a different pool wallet so the chart does not show one giant wallet dumping.",
  },
];

export const SMART_SELL_LANDING_FAQ = [
  {
    q: "What does Smart Sell do?",
    a: "When someone buys on your chart and market cap is at your target, it sells a slice of that buy from your deposit — through a fresh wallet — and sends SOL back.",
  },
  {
    q: "Does Smart Sell use a smart contract?",
    a: "No. You fund a deposit address. The bot watches buys, sells from that bag through managed wallets, then returns SOL to the deposit. Withdraw with Refund.",
  },
  {
    q: "What buys are ignored?",
    a: "Buys under 0.1 SOL, buys from wallets you chose to skip, and any buy while live market cap is still below your target.",
  },
  {
    q: "What if the market cap is already above my target when I start?",
    a: "The target is a USD gate, not a wait-for-a-dip. If live market cap is already at or above it, qualifying buys can trigger sells right away.",
  },
  {
    q: "What does “a chosen percent of the buy” mean?",
    a: "If someone buys with 1 SOL and you set 25%, Smart Sell aims to sell about 0.25 SOL of tokens — not 25% of your bag.",
  },
  {
    q: "How big can one sell be?",
    a: "Each sell is also capped at your max-sell-per-trade setting (default 10% of the tokens left in the deposit), so one reaction cannot empty the bag.",
  },
  {
    q: "What is max sell per trade?",
    a: "It caps how much of your token bag is sold on one qualifying buy, no matter how large the buy is. That protects your exit price from one oversized sale. Default is 10%. You can set 1–100% when you create the bot or later in settings.",
  },
  {
    q: "Why skip wallets?",
    a: "Add team or bot wallets so those buys never trigger a sell — you don’t sell into yourself.",
  },
  {
    q: "What do I put in the deposit wallet?",
    a: "The tokens you want to sell — no SOL deposit required. Swaps are gasless-first. Sold SOL comes back to that same deposit.",
  },
];

/** Form field helper text (plain language). */
export const SMART_SELL_FORM_LABELS = {
  mint: {
    label: "Token",
    hint: "Step 1 — enter the token address",
    placeholder: "Paste mint address",
    selectFromWallet: "Wallet",
    connectWalletFirst: "Connect a wallet to pick a token.",
  },
  pool: {
    label: "Choose the pool this token trades on",
    hint: "Choose the pool this token trades on",
  },
  targetMcap: {
    label: "Start selling when market cap reaches (USD)",
    hint: "Step 2 — set the market cap to hit",
    placeholder: "e.g. 500000",
    currentLabel: "Current market cap",
    currentLoading: "Loading current market cap…",
    currentUnavailable: "Current market cap unavailable for this pool",
    aboveTarget: "Live mcap is at or above your target — sells can start.",
    belowTarget: "Live mcap is still below your target — the bot will wait.",
  },
  sellPct: {
    label: "Smart Sell percentage",
    hint: "Step 3 — smart sell percentage",
    detail:
      "Sells this percent of each buy that triggers it, not your total deposit.",
    placeholder: "e.g. 30",
    warning: "Type between 5 and 100 percent.",
  },
  maxSellPct: {
    label: "Max sell per trade",
    hint: "Max of your bag per sell",
    detail:
      "Never sell more than this percent of your remaining tokens in one trade, even if the buy is large.",
    placeholder: "e.g. 10",
    warning: "Type between 1 and 100 percent.",
  },
  whitelist: {
    label: "Wallets to skip (optional)",
    hint: "One address per line. Buys from these wallets never trigger a sell.",
    placeholder: "e.g.\n7xKXt…9pQm\n4Nd1h…2kL8",
  },
};

/** Bot card UI labels — keep short and obvious. */
export const SMART_SELL_CARD_LABELS = {
  badge: "Smart Sell",
  soldTitle: "Sold so far",
  soldTokens: "Tokens sold",
  soldSol: "SOL received",
  sellsDone: "Sells done",
  bagTitle: "What’s left in the bag",
  bagTokens: "Tokens left",
  bagSol: "SOL in bag",
  walletsTitle: "Wallets",
  skipWallets: "Skip wallets (whitelist)",
  sellWalletsUsed: "Sell wallets used",
  buyersHit: "Buyers we sold into",
  targetMcap: "Your target mcap",
  liveMcap: "Live mcap",
  sellPct: "Sell (of buy)",
  sellPctHint: "Type between 5 and 100 percent",
  sellPctSave: "Save %",
  maxSellPct: "Max sell (of bag)",
  maxSellPctHint: "Cap on your bag sold in one trade (1–100%)",
  maxSellPctSave: "Save max %",
  deposit: "Deposit wallet",
  fundHint: "Add tokens, then press Start.",
  dryRun:
    "Practice mode (dry-run): buys are logged but no real sells happen yet.",
  emptyHistory: "No sells yet — waiting for qualifying buys.",
  fillsTitle: "Last buys",
  fillsEmpty: "No buys seen yet — waiting for chart activity.",
  fillChecked: "Sold",
  fillSkipped: "Skipped",
  recentTxTitle: "Last 3 transactions",
  recentTxEmpty: "No buys seen yet — waiting for chart activity.",
  buyLabel: "Buy",
  soldLabel: "Bot sold",
  skipIgnoredWallet: "Ignored wallet (whitelist)",
  skipBelowMin: "Buy too small",
  skipBelowMcap: "Below target mcap",
  skipRateLimit: "Rate limit",
  skipMcapUnavailable: "Couldn’t read mcap",
  skipOther: "Skipped",
  refundTitle: "Withdraw",
  refundSolProceeds: "Withdraw sold SOL",
  refundSolProceedsHint:
    "Send sold SOL to your wallet.",
  refundFull: "Delete bot & full withdraw",
  refundFullHint:
    "Delete Smart Sell, send remaining tokens + SOL home (tiny dust left).",
  refundWalletLabel: "Withdraw to wallet",
  refundWalletPlaceholder: "Your Solana wallet address",
};
